import { useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { motion } from 'framer-motion';

interface Props {
  /** Valeur finale à atteindre. */
  to: number;
  /** Préfixe optionnel (ex: ""). */
  prefix?: string;
  /** Suffixe optionnel (ex: "+"). */
  suffix?: string;
  /** Durée de l'animation en secondes. */
  duration?: number;
  /** Nombre de décimales. */
  decimals?: number;
  className?: string;
}

/**
 * Anime un nombre de 0 → `to` quand l'élément entre dans le viewport.
 * Idéal pour les statistiques marquantes (60+ Whiskys, 8 Microbrasseries…).
 */
export default function CountUp({
  to,
  prefix = '',
  suffix = '',
  duration = 1.6,
  decimals = 0,
  className,
}: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, {
    stiffness: 50,
    damping: 20,
    duration: duration * 1000,
  });
  const display = useTransform(spring, (latest) =>
    `${prefix}${latest.toFixed(decimals)}${suffix}`
  );

  useEffect(() => {
    if (inView) motionValue.set(to);
  }, [inView, motionValue, to]);

  return (
    <motion.span ref={ref} className={className}>
      {display}
    </motion.span>
  );
}
